"use client";
import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useFieldArray } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { createOrder } from "@/server/Orders";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { signIn, useSession } from "next-auth/react";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Separator } from "./ui/separator";
import { tunisiaGovernates } from "@/lib/tunisiaMap";
import { PlusCircle, Trash2, X } from "lucide-react";
import { Facebook, Instagram, MessageCircle } from "lucide-react";

const socialMediaSchema = z.object({
  platform: z.enum(["instagram", "facebook", "whatsapp"]),
  handle: z.string().min(1, "Handle is required"),
});

const FormSchema = z
  .object({
    fullName: z
      .string()
      .min(2, { message: "Full name must be at least 2 characters." }),
    phoneNumber: z
      .string()
      .min(8, { message: "Phone number must be at least 8 characters." }),
    additionalPhoneNumber: z
      .string()
      .min(8, { message: "Phone number must be at least 8 characters." })
      .optional(),
    socialMedia: z
      .array(socialMediaSchema)
      .min(1, "At least one social media contact is required"),
    additionalNotes: z.string().optional(),
    deliveryMethod: z.enum(["pickup", "delivery"]),
    governorate: z.string().optional(),
    fullAddress: z.string().optional(),
  })
  .refine(
    (data) => {
      if (data.deliveryMethod === "delivery") {
        return !!data.fullAddress;
      }
      return true;
    },
    {
      message: "Full address is required for delivery",
      path: ["fullAddress"],
    }
  )
  .refine(
    (data) => {
      const platforms = data.socialMedia.map((sm) => sm.platform);
      return new Set(platforms).size === platforms.length;
    },
    {
      message: "Each social media platform can only be selected once",
      path: ["socialMedia"],
    }
  );

function getSocialMediaPlaceholder(platform: string) {
  switch (platform) {
    case "instagram":
      return "@username";
    case "facebook":
      return "username";
    case "whatsapp":
      return "+216...";
    default:
      return "";
  }
}

function getSocialMediaIcon(platform: string) {
  switch (platform) {
    case "instagram":
      return <Instagram className="h-4 w-4 mr-2" />;
    case "facebook":
      return <Facebook className="h-4 w-4 mr-2" />;
    case "whatsapp":
      return <MessageCircle className="h-4 w-4 mr-2" />;
    default:
      return null;
  }
}

interface CheckoutFormProps {
  onBack: () => void;
  cart: {
    id: string;
    name: string;
    size: string;
    quantity: number;
    price: number;
  }[];
  onOrderPlaced: (orderId: string) => void;
  isSheet?: boolean;
}

export default function CheckoutForm({
  onBack,
  cart,
  onOrderPlaced,
  isSheet = false,
}: CheckoutFormProps) {
  const { data: session } = useSession();
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      fullName: "",
      phoneNumber: "",
      additionalPhoneNumber: "",
      socialMedia: [{ platform: "instagram", handle: "" }],
      additionalNotes: "",
      deliveryMethod: "pickup",
      governorate: "",
      fullAddress: "",
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "socialMedia",
  });

  const deliveryMethod = form.watch("deliveryMethod");
  const socialMedia = form.watch("socialMedia");

  const availablePlatforms = ["instagram", "facebook", "whatsapp"].filter(
    (platform) => !socialMedia.some((sm) => sm.platform === platform)
  );

  const onSubmit = async (data: z.infer<typeof FormSchema>) => {
    setIsLoading(true);
    try {
      const totalAmount =
        cart.reduce((total, item) => total + item.price * item.quantity, 0) +
        (data.deliveryMethod === "delivery" ? 7 : 0);

      const orderData = {
        ...data,
        instagram: data.socialMedia.find((sm) => sm.platform === "instagram")
          ?.handle,
        facebook: data.socialMedia.find((sm) => sm.platform === "facebook")
          ?.handle,
        whatsapp: data.socialMedia.find((sm) => sm.platform === "whatsapp")
          ?.handle,
        items: cart,
        totalAmount,
      };

      const result = await createOrder(orderData);

      if (result.success && result.orderId) {
        onOrderPlaced(result.orderId);
      } else {
        throw new Error(result.error || "Failed to create order");
      }
    } catch (error) {
      console.error("Error placing order:", error);
      // Handle error (e.g., show error message to user)
    } finally {
      setIsLoading(false);
    }
  };

  const totalPrice = cart.reduce(
    (total, item) => total + item.price * item.quantity,
    0
  );
  const deliveryFee = deliveryMethod === "delivery" ? 7 : 0;
  const finalTotal = totalPrice + deliveryFee;

  const formContent = (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="px-1 space-y-2">
        {session ? (
          <div className="text-xs font-mono flex items-center justify-center gap-1">
            {`You're signed in as `}
            {session.user?.image && (
              <Avatar className="w-6 h-6">
                <AvatarImage
                  src={session.user?.image}
                  alt={session.user?.name || undefined}
                />
                <AvatarFallback />
              </Avatar>
            )}
            {session.user?.name?.toUpperCase() || ""}
          </div>
        ) : (
          <div
            className="text-xs font-mono flex gap-1 text-center items-center justify-center cursor-pointer"
            onClick={() => signIn()}
          >
            <span>You're not loggeed in</span>
            <span className="text-[#FF0000] underline">Log in</span>
          </div>
        )}

        <div className="space-y-4">
          <FormField
            control={form.control}
            name="fullName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Full Name</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="phoneNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone Number</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="additionalPhoneNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Additional Phone Number</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="space-y-4">
            {fields.map((field, index) => (
              <div key={field.id} className="flex items-end space-x-2">
                <FormField
                  control={form.control}
                  name={`socialMedia.${index}.platform`}
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <Select
                        value={field.value}
                        onValueChange={(value) => {
                          field.onChange(value);
                          form.trigger(`socialMedia.${index}.platform`);
                        }}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue>
                              <div className="flex items-center">
                                {getSocialMediaIcon(field.value)}
                                {field.value.charAt(0).toUpperCase() +
                                  field.value.slice(1)}
                              </div>
                            </SelectValue>
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {index === 0 ? (
                            <>
                              <SelectItem value="instagram">
                                <div className="flex items-center">
                                  <Instagram className="h-4 w-4 mr-2" />
                                  Instagram
                                </div>
                              </SelectItem>
                              <SelectItem value="facebook">
                                <div className="flex items-center">
                                  <Facebook className="h-4 w-4 mr-2" />
                                  Facebook
                                </div>
                              </SelectItem>
                              <SelectItem value="whatsapp">
                                <div className="flex items-center">
                                  <MessageCircle className="h-4 w-4 mr-2" />
                                  WhatsApp
                                </div>
                              </SelectItem>
                            </>
                          ) : (
                            availablePlatforms.map((platform) => (
                              <SelectItem key={platform} value={platform}>
                                <div className="flex items-center">
                                  {getSocialMediaIcon(platform)}
                                  {platform.charAt(0).toUpperCase() +
                                    platform.slice(1)}
                                </div>
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name={`socialMedia.${index}.handle`}
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormControl>
                        <Input
                          {...field}
                          placeholder={getSocialMediaPlaceholder(
                            form.watch(`socialMedia.${index}.platform`)
                          )}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {fields.length > 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => remove(index)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
            {fields.length < 3 && availablePlatforms.length > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="w-full text-xs"
                onClick={() =>
                  append({
                    platform: availablePlatforms[0] as
                      | "instagram"
                      | "facebook"
                      | "whatsapp",
                    handle: "",
                  })
                }
              >
                <PlusCircle className="h-4 w-4 mr-2" />
                Add Social Media
              </Button>
            )}
          </div>
        </div>

        <Separator />

        <FormField
          control={form.control}
          name="deliveryMethod"
          render={({ field }) => (
            <FormItem className="space-y-3">
              <FormLabel>Delivery Method</FormLabel>
              <FormControl>
                <RadioGroup
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  className="flex flex-col space-y-1"
                >
                  <FormItem className="flex items-center space-x-3 space-y-0">
                    <FormControl>
                      <RadioGroupItem value="pickup" />
                    </FormControl>
                    <FormLabel className="font-normal">
                      Pickup near Bardo/Downtown
                    </FormLabel>
                  </FormItem>
                  <FormItem className="flex items-center space-x-3 space-y-0">
                    <FormControl>
                      <RadioGroupItem value="delivery" />
                    </FormControl>
                    <FormLabel className="font-normal">
                      Delivery all Tunisia{" "}
                      <span className="text-xs text-muted-foreground">
                        (+7 TND)
                      </span>
                    </FormLabel>
                  </FormItem>
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {deliveryMethod === "delivery" && (
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="governorate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Governorate</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select governorate" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {tunisiaGovernates.map((governorate) => (
                        <SelectItem key={governorate} value={governorate}>
                          {governorate}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="fullAddress"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Full Address</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}

        <FormField
          control={form.control}
          name="additionalNotes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Additional Notes</FormLabel>
              <FormControl>
                <Textarea {...field} className="h-20" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="space-y-2 pt-2 text-xs lg:text-base">
          <h2 className="font-semibold">Order Summary</h2>
          {cart.map((item) => (
            <div
              key={`${item.id}-${item.size}`}
              className="flex justify-between items-center"
            >
              <span>
                {item.name} — Size: {item.size}
              </span>
              <span>
                {item.quantity} x {item.price} TND
              </span>
            </div>
          ))}
          {deliveryMethod === "delivery" && (
            <div className="flex justify-between items-center">
              <span>Delivery Fee</span>
              <span>7 TND</span>
            </div>
          )}
          <div className="flex justify-between items-center font-semibold">
            <span>Total</span>
            <span>{finalTotal} TND</span>
          </div>
        </div>

        <div className="space-y-2">
          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? "Placing Order..." : "Place Order"}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={onBack}
          >
            Back to Cart
          </Button>
        </div>
      </form>
    </Form>
  );

  return isSheet ? (
    <ScrollArea className="h-[calc(100vh-4rem)] pr-4">{formContent}</ScrollArea>
  ) : (
    formContent
  );
}
